// Add-coverage-line modal — FE-SCRM-015.
//
// Producer picks ``line_type`` and (optionally) seeds the row with the
// incumbent's carrier + premium + lives + target-carrier list. ``outcome``
// is intentionally not part of the create form: a new line always starts
// at ``pending`` per the BE default — outcome transitions go through
// the Edit modal so the won/lost ceremony is explicit.

import { useEffect, useState } from "react";

import { Modal } from "@/design-system";

import {
  COVERAGE_LINE_TYPE_LABEL,
  QUOTE_STATUS_LABEL,
} from "./labels";
import {
  COVERAGE_LINE_TYPES,
  QUOTE_STATUSES,
  type AddCoverageLineInput,
  type CoverageLineType,
  type QuoteStatus,
} from "./types";


interface Props {
  open: boolean;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onSubmit: (input: AddCoverageLineInput) => void;
}


export function AddCoverageLineModal({
  open, isSubmitting, errorMessage, onClose, onSubmit,
}: Props) {
  const [lineType, setLineType] = useState<CoverageLineType>("medical");
  const [currentCarrier, setCurrentCarrier] = useState("");
  const [premiumStr, setPremiumStr] = useState("");
  const [livesStr, setLivesStr] = useState("");
  const [targetCarriers, setTargetCarriers] = useState("");
  const [quoteStatus, setQuoteStatus] = useState<QuoteStatus>("not_started");
  const [notes, setNotes] = useState("");

  // Reset on each open so a previous attempt doesn't bleed in.
  useEffect(() => {
    if (!open) return;
    setLineType("medical");
    setCurrentCarrier("");
    setPremiumStr("");
    setLivesStr("");
    setTargetCarriers("");
    setQuoteStatus("not_started");
    setNotes("");
  }, [open]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const targets = targetCarriers
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    // Premium is entered in whole dollars; the BE stores cents so we
    // round-trip on the way in. Empty input means "not yet known."
    const premiumDollars = premiumStr.trim();
    const premiumCents = premiumDollars
      ? Math.round(Number(premiumDollars) * 100)
      : null;
    const livesTrim = livesStr.trim();
    const lives = livesTrim ? Number(livesTrim) : null;
    onSubmit({
      line_type: lineType,
      current_carrier: currentCarrier.trim() || null,
      current_premium_annualized_cents:
        Number.isFinite(premiumCents) ? premiumCents : null,
      current_lives_covered:
        lives !== null && Number.isFinite(lives) ? lives : null,
      target_carriers: targets,
      quote_status: quoteStatus,
      notes: notes.trim() || null,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add coverage line"
      size="md"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            data-testid="add-coverage-line-cancel"
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-primary hover:bg-recessed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-coverage-line-form"
            disabled={isSubmitting}
            data-testid="add-coverage-line-submit"
            className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90 disabled:opacity-60"
          >
            {isSubmitting ? "Adding…" : "Add coverage line"}
          </button>
        </>
      }
    >
      <form
        id="add-coverage-line-form"
        data-testid="add-coverage-line-form"
        onSubmit={submit}
        className="flex flex-col gap-3"
      >
        {errorMessage && (
          <div
            role="alert"
            className="rounded-md bg-ember/15 px-3 py-2 text-sm text-ember"
          >
            {errorMessage}
          </div>
        )}

        <Field label="Coverage type">
          <select
            data-testid="add-coverage-line-type"
            value={lineType}
            onChange={(e) => setLineType(e.target.value as CoverageLineType)}
            className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm"
          >
            {COVERAGE_LINE_TYPES.map((t) => (
              <option key={t} value={t}>
                {COVERAGE_LINE_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Current carrier (incumbent)">
          <input
            data-testid="add-coverage-line-current-carrier"
            type="text"
            value={currentCarrier}
            onChange={(e) => setCurrentCarrier(e.target.value)}
            placeholder="e.g. Aetna"
            className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Annual premium ($)">
            <input
              data-testid="add-coverage-line-premium"
              type="number"
              min="0"
              step="1"
              value={premiumStr}
              onChange={(e) => setPremiumStr(e.target.value)}
              placeholder="120000"
              className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm"
            />
          </Field>
          <Field label="Lives covered">
            <input
              data-testid="add-coverage-line-lives"
              type="number"
              min="0"
              step="1"
              value={livesStr}
              onChange={(e) => setLivesStr(e.target.value)}
              placeholder="175"
              className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm"
            />
          </Field>
        </div>

        <Field
          label="Target carriers"
          hint="Comma-separated list of carriers you're quoting."
        >
          <input
            data-testid="add-coverage-line-target-carriers"
            type="text"
            value={targetCarriers}
            onChange={(e) => setTargetCarriers(e.target.value)}
            placeholder="Guardian, Principal"
            className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm"
          />
        </Field>

        <Field label="Quote status">
          <select
            data-testid="add-coverage-line-quote-status"
            value={quoteStatus}
            onChange={(e) => setQuoteStatus(e.target.value as QuoteStatus)}
            className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm"
          >
            {QUOTE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {QUOTE_STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Notes">
          <textarea
            data-testid="add-coverage-line-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm"
          />
        </Field>
      </form>
    </Modal>
  );
}


function Field({
  label, hint, children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-text-primary">{label}</span>
      {children}
      {hint ? (
        <span className="text-xs text-text-tertiary">{hint}</span>
      ) : null}
    </label>
  );
}
